import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { Button, VerticalExpander, Text, InfoPill } from '../../../elementals';
import * as constants from '../../../../constants';
import * as hooks from '../../../../hooks';
import * as graph from '@adc/sdk';
import classnames from 'classnames/bind';
import _ from 'lodash';
import styles from './styles.scss';
import SealedBidForm from '../bidForm';
import { useSealedBidForm } from '../../../../utils/sealedBid';
import GeneralModal from '../../../overlays/generalModal';
const cx = classnames.bind(styles);
const { ColorNames } = constants;
const UPDATE_OFFER_TEXT = 'Update Offer';
const UpdateOfferModalButton = ({ submitCallback, listing, segment, confirmButtonText = 'Send Updated Offer', modalTitle, headers, bid, readOnly = false, cancelCallback = () => false, disabled = false, viewingFrom = graph.enums.CurrentTargetAudience.Seller, classNames = {}, buttonProps = {}, actions, preventClose = true, formRef, ...rest }) => {
    const [showModal, setShowModal] = React.useState(false);
    const [isUpdating, setUpdating] = React.useState(false);
    const isBuyerView = graph.enums.CurrentTargetAudience.Buyer === viewingFrom;
    const isDisabled = useMemo(() => disabled ||
        isUpdating ||
        (isBuyerView &&
            segment &&
            [
                graph.enums.OnlineAuctionSegmentStatus.HighestAndBestDecision,
            ].includes(segment?.segment_status)), [disabled, segment, isBuyerView, isUpdating]);
    const { data, errors, dirty, config, components, reset, validate } = useSealedBidForm({
        bid,
        headers,
        listing,
        segment,
        readOnly,
        canUpdateMyOffer: true,
        viewingFrom,
    });
    const breakpoint = hooks.useBreakpoints();
    const isMobile = breakpoint === 'mobile';
    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        reset();
    }, [reset]);
    const handleCancel = useCallback(() => {
        handleCloseModal();
        cancelCallback();
    }, [handleCloseModal, cancelCallback]);
    const handleSubmit = useCallback(async () => {
        if (!data || !validate())
            return;
        setUpdating(true);
        await submitCallback({
            sealedBidInput: data,
        });
        setUpdating(false);
        handleCloseModal();
    }, [data, validate, submitCallback, handleCloseModal]);
    const header = useMemo(() => {
        if (modalTitle)
            return modalTitle;
        return (_jsx(_Fragment, { children: _jsxs("div", { className: cx('modal-title'), children: [_jsx(Text, { size: 'big', color: 'black', children: "Update Offer" }), !isMobile && (_jsxs(_Fragment, { children: [_jsx(Text, { size: 'small', color: 'gray', children: "Use the fields below to update your offer" }), _jsx("div", { className: 'u-mt-2 u-mb-1', children: _jsx(InfoPill, { size: 'medium', includeBorder: true, backgroundColor: ColorNames.InfoExtraLight, foregroundColor: ColorNames.Info, iconName: 'medal', label: _jsxs(Text, { size: 'small', color: ColorNames.Info, children: ["Update your offer to see where you stand on the", ' ', _jsx("strong", { children: "Leaderboard!" })] }) }) })] }))] }) }));
    }, [modalTitle]);
    const handleShowModal = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
    }, []);
    const actionButtons = useMemo(() => {
        if (actions)
            return actions;
        return [
            {
                label: 'Cancel',
                elmId: 'update_offer_cancel_button',
                onClick: handleCancel,
            },
            {
                label: confirmButtonText,
                onClick: handleSubmit,
                disabled: isDisabled || !dirty || !_.isEmpty(errors),
                loading: isUpdating,
                elmId: 'update_offer_confirm_button',
                isCountdownButton: true,
                reInitialize: true,
                role: 'primary',
            },
        ];
    }, [
        actions,
        confirmButtonText,
        isDisabled,
        isUpdating,
        dirty,
        errors,
        handleCancel,
        handleSubmit,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', role: 'secondary', ..._.omit(buttonProps, 'label'), onClick: handleShowModal, className: cx('large-fixed-button', classNames?.button), elmId: `update_offer_button${isDisabled ? '_disabled' : ''}`, disabled: isDisabled, children: buttonProps?.label ?? UPDATE_OFFER_TEXT }), _jsx(GeneralModal, { ...rest, open: showModal, title: header, className: cx('update-offer-modal', classNames?.modal), elmId: 'update_offer_elm_id', mobileTitleSkin: 'light', onClose: handleCancel, size: 'large', actions: actionButtons, preventClose: preventClose, children: _jsx(VerticalExpander, { children: _jsx(SealedBidForm, { className: classNames?.form, config: config, slots: {
                            components,
                        }, bid: bid, listing: listing, readOnly: readOnly, viewingFrom: viewingFrom, onSubmit: handleSubmit, formRef: formRef }) }) })] }));
};
export default UpdateOfferModalButton;
//# sourceMappingURL=index.js.map